import { useState, type FormEvent, type ReactNode } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { FLOW_META, type FlowType } from '../lib/types';
import { changePassword, ApiError } from '../lib/api';

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderTop: '1px solid var(--p-border)',
      fontSize: 13,
    }}
  >
    <span style={{ color: 'var(--p-muted-fg)' }}>{label}</span>
    <span>{value}</span>
  </div>
);

export const SettingsPage = () => (
  <Page>
    <PageHeader title="Settings" sub="Integrations and billing schedule" />

    <div className="grid grid-2">
      <PCard style={{ padding: 20 }}>
        <div className="section-title">Payment integrations</div>
        <Row label="Stripe (platform fee)" value={<StatusPill tone="success">Connected</StatusPill>} />
        <Row label="Hyperswitch (pharmacy + shipping)" value={<StatusPill tone="success">Connected</StatusPill>} />
        <Row label="Stripe webhook secret" value={<span className="mono" style={{ color: 'var(--p-muted-fg)' }}>whsec_•••••</span>} />
      </PCard>

      <PCard style={{ padding: 20 }}>
        <div className="section-title">Source connections</div>
        <Row label="Billing DB (read-write)" value={<StatusPill tone="success">Configured</StatusPill>} />
        <Row label="Core DB (read-only)" value={<StatusPill tone="success">Configured</StatusPill>} />
        <Row label="Pharmacy DB (read-only)" value={<StatusPill tone="success">Configured</StatusPill>} />
        <Row label="Accounting (shipping source)" value={<StatusPill tone="attention">Not wired</StatusPill>} />
      </PCard>
    </div>

    <div className="grid grid-2">
      <ChangePasswordCard />
    </div>

    <div>
      <div className="section-title">Billing schedule</div>
      <div className="grid grid-3">
        {(Object.keys(FLOW_META) as FlowType[]).map((f) => {
          const m = FLOW_META[f];
          return (
            <PCard key={f} style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{m.label} fee</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--p-muted-fg)', marginTop: 8 }}>{m.cadence}</div>
              <div style={{ fontSize: 13, color: 'var(--p-muted-fg)', marginTop: 2 }}>Rails: {m.rails}</div>
            </PCard>
          );
        })}
      </div>
    </div>
  </Page>
);

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) {
      setMsg({ tone: 'err', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (next !== confirm) {
      setMsg({ tone: 'err', text: 'New password and confirmation do not match.' });
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setMsg({ tone: 'ok', text: 'Password changed. Other sessions were signed out.' });
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof ApiError ? err.message : 'Could not change password' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <PCard style={{ padding: 20 }}>
      <div className="section-title">Change password</div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
        <PwField label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" />
        <PwField label="New password" value={next} onChange={setNext} autoComplete="new-password" />
        <PwField label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
        {msg && (
          <div style={{ fontSize: 13, color: msg.tone === 'ok' ? 'var(--p-muted-fg)' : 'var(--status-error-ink,#b91c1c)' }}>
            {msg.text}
          </div>
        )}
        <div>
          <button type="submit" className="btn btn--primary" disabled={busy || !current || !next || !confirm}>
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </div>
      </form>
    </PCard>
  );
}

const PwField = ({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--p-muted-fg)' }}>
    {label}
    <input
      className="select"
      type="password"
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);
